// Scroll Heading Handler
// - Zerlegt .heading h2 in einzelne Wörter
// - Animiert Wörter einzeln beim Scrollen (blur + skew + opacity)
// - Start: wenn h2 sichtbar wird (unten im Viewport)
// - Ende: wenn h2 oberhalb der Bildschirmmitte

interface WordSpan {
	span: HTMLSpanElement;
}

interface HeadingConfig {
	heading: HTMLElement;
	h2: HTMLElement;
	wordSpans: WordSpan[];
}

export function initScrollHeading(): void {
	const headings = document.querySelectorAll('.heading h2[data-animate]');
	
	if (!headings.length) return;

	const configs: HeadingConfig[] = Array.from(headings)
		.map(h2 => {
			const heading = h2.closest('.heading');
			if (!heading) return null;

			const originalText = h2.textContent || '';
			const words = originalText.trim().split(/\s+/);

			// h2 leeren und mit Spans füllen
			h2.innerHTML = '';
			const wordSpans: WordSpan[] = words.map(word => {
				const span = document.createElement('span');
				span.textContent = word;
				span.style.display = 'inline-block';
				span.style.marginRight = '0.3em';
				span.style.willChange = 'filter, transform, opacity';
				h2.appendChild(span);

				return { span };
			});

			return {
				heading: heading as HTMLElement,
				h2: h2 as HTMLElement,
				wordSpans,
			};
		})
		.filter((config): config is HeadingConfig => config !== null);

	if (!configs.length) return;

	// Update Heading-Animation kontinuierlich beim Scroll
	const updateHeadingAnimation = () => {
		configs.forEach(({ heading, wordSpans }) => {
			// Bounding Rect vom Heading
			const rect = heading.getBoundingClientRect();
			const headingCenter = rect.top + rect.height / 2;

			// Viewpoint-Breiten
			const viewportHeight = window.innerHeight;
			
			// Start: Element kommt unten in Sicht (viewportHeight)
			// Ende: Element ist oberhalb der Bildschirmmitte (viewportHeight / 2)
			const startPoint = viewportHeight; // Unten - Animation beginnt
			const endPoint = viewportHeight / 2; // Mitte - Animation abgeschlossen

			// Fortschritt je näher Element nach oben kommt
			const distanceFromMidpoint = startPoint - headingCenter;
			const activationRange = startPoint - endPoint;

			// Fortschritt clamped zwischen 0 und 1
			let overallProgress = distanceFromMidpoint / activationRange;
			overallProgress = Math.max(0, Math.min(1, overallProgress));

			// Berechne gesamte Stagger-Verzögerung (vom ersten bis letzten Wort)
			const totalWordDelay = (wordSpans.length - 1) * 0.08;
			
			// Skaliere overallProgress so, dass die Animation über ALLE Wörter läuft
			const adjustedProgress = overallProgress * (1 + totalWordDelay);

			// Für jedes Wort: individueller Fortschritt
			wordSpans.forEach((word, index) => {
				// Jedes Wort animiert mit Stagger-Effekt
				const wordDelay = index * 0.08; // 8% Stagger pro Wort
				
				// Fortschritt für dieses Wort (0 bis 1, mit Stagger)
				const wordProgress = Math.max(0, Math.min(1, adjustedProgress - wordDelay));

				// Interpoliere Blur-Effekt
				const blur = 8 * (1 - wordProgress); // Von 8px zu 0px
				const skew = -20 * (1 - wordProgress); // Von -20deg zu 0deg
				const opacity = wordProgress; // Von 0 zu 1

				// CSS anwenden
				word.span.style.filter = `blur(${blur}px)`;
				word.span.style.transform = `skew(${skew}deg)`;
				word.span.style.opacity = String(opacity);
			});
		});
	};

	// Mit requestAnimationFrame für optimale Performance
	const animationLoop = () => {
		updateHeadingAnimation();
		requestAnimationFrame(animationLoop);
	};

	animationLoop();
}
